import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Check, Clock } from 'lucide-react';
import type { ManagementNtpResult } from '@simpleacs/shared';
import { api } from '../../lib/api';
import { useTasks } from '../../lib/tasks';
import { WizardDialog } from '../../components/WizardDialog';
import { Button, ErrorNotice, Spinner } from '../../components/ui';

/**
 * Keep this OLT's clock from the platform's own host, over its management
 * path (operator, 2026-10-06: "build the route and vrf ntp into the
 * platform").
 *
 * Lagos's C650 is why: its management port (VRF `mng`) had no route past its
 * own subnet, so its public time servers were never reachable. The check is a
 * read and stays in the dialog: it pings the platform's host from the chassis
 * and says, in words, what would change — a host route through the site
 * router where the ping fails, and the platform's host as a time server.
 * Where the management subnet holds more than one device the operator names
 * the site router; a guessed gateway is how a chassis loses its management
 * path. The write CLOSES the dialog and runs in the tasks tray, and no chassis
 * line is shown (no command previews, 2026-09-19).
 */
export function ManagementNtpDialog({
  oltId,
  oltName,
  onClose,
}: {
  oltId: string;
  oltName: string;
  onClose: () => void;
}) {
  const tasks = useTasks();
  /** The router the operator picked from the list, and the one the last check was asked with. */
  const [nextHop, setNextHop] = useState<string | null>(null);
  const [checkedWith, setCheckedWith] = useState<string | null>(null);

  // The check is a READ (a dry run): a query, so it runs once as the dialog
  // opens, survives a remount, and runs again only when a router is named.
  const check = useQuery({
    queryKey: ['olt', oltId, 'management-ntp', checkedWith],
    queryFn: () =>
      api.post<ManagementNtpResult>(`/olts/${encodeURIComponent(oltId)}/time/management-ntp`, {
        dryRun: true,
        ...(checkedWith ? { nextHop: checkedWith } : {}),
      }),
    retry: false,
    staleTime: 0,
    gcTime: 0,
    refetchOnWindowFocus: false,
  });
  const plan = check.data ?? null;

  const nothingToDo = plan !== null && plan.commands.length === 0 && plan.blockers.length === 0;
  const choosing = plan !== null && plan.blockers.length > 0 && plan.nextHopCandidates.length > 1;
  const ready = plan !== null && plan.blockers.length === 0 && plan.commands.length > 0;

  const startApply = () => {
    if (!plan) return;
    const hop = plan.nextHopSource === 'given' ? plan.nextHop : null;
    onClose();
    tasks.start<ManagementNtpResult>({
      label: `Keep time from the platform — ${oltName}`,
      href: `/olts/${encodeURIComponent(oltId)}`,
      run: async () => {
        const result = await api.post<ManagementNtpResult>(`/olts/${encodeURIComponent(oltId)}/time/management-ntp`, {
          dryRun: false,
          ...(hop ? { nextHop: hop } : {}),
        });
        // A line the chassis refused is a failed task, in the chassis's own words.
        if (result.refused.length > 0) throw new Error(result.note);
        return result;
      },
      done: (result) => result.note,
      invalidate: [['olt', oltId, 'time']],
    });
  };

  return (
    <WizardDialog
      title={
        <span className="flex items-center gap-2">
          <Clock className="size-4 text-muted" />
          Keep time from the platform — {oltName}
        </span>
      }
      subtitle="The chassis asks the platform's own server the time, over the same management path SimpleACS reaches it by. Where it cannot reach that server now, a route for that one address is added through the site router. Nothing is removed."
      maxWidth="xl"
      bodyClassName="grid gap-3 p-5"
      steps={[
        { key: 'check', title: 'Check' },
        { key: 'review', title: 'Review' },
      ]}
      currentStep={plan ? 1 : 0}
      onClose={onClose}
      footer={
        <div className="flex items-center justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>
            {nothingToDo ? 'Close' : 'Cancel'}
          </Button>
          {choosing && (
            <Button variant="secondary" disabled={!nextHop} loading={check.isFetching} onClick={() => setCheckedWith(nextHop)}>
              Use this router
            </Button>
          )}
          {!nothingToDo && (
            <Button variant="primary" icon={<Check className="size-4" />} disabled={!ready || check.isFetching} onClick={startApply}>
              Apply
            </Button>
          )}
        </div>
      }
    >
      {check.isFetching && !plan && (
        <p className="flex items-center gap-2 text-sm text-muted">
          <Spinner /> Asking {oltName} whether it reaches the platform's server…
        </p>
      )}
      {check.isError && <ErrorNotice error={check.error} />}
      {plan && (
        <>
          <p className="text-sm text-ink">{plan.note}</p>
          <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm">
            <dt className="text-muted">Platform server</dt>
            <dd className="font-mono">{plan.server}</dd>
            <dt className="text-muted">Management port</dt>
            <dd>
              {plan.interface ?? 'not listed'}
              {plan.vrf ? ` · VRF ${plan.vrf}` : ''}
            </dd>
            <dt className="text-muted">Reaches it now</dt>
            <dd>{plan.reachable === true ? 'yes' : plan.reachable === false ? 'no' : 'could not tell'}</dd>
            {plan.needsRoute && plan.nextHop && (
              <>
                <dt className="text-muted">Through</dt>
                <dd>
                  <span className="font-mono">{plan.nextHop}</span>
                  {plan.nextHopSource === 'arp' ? ' — the only other device on the management subnet' : ' — named here'}
                </dd>
              </>
            )}
          </dl>
          {choosing && (
            <fieldset className="grid gap-1.5">
              <legend className="mb-1 text-sm text-muted">Which of these is the site router?</legend>
              {plan.nextHopCandidates.map((ip) => (
                <label key={ip} className="flex items-center gap-2 text-sm">
                  <input type="radio" name="next-hop" checked={nextHop === ip} onChange={() => setNextHop(ip)} />
                  <span className="font-mono">{ip}</span>
                </label>
              ))}
            </fieldset>
          )}
          {[...plan.warnings, ...plan.unverified].map((w) => (
            <p key={w} className="text-xs text-muted">
              {w}
            </p>
          ))}
        </>
      )}
    </WizardDialog>
  );
}
